import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './auth.context';
import { useToast } from '../hooks/use-toast';

// Define the shape of a search result
interface OADSearchResult {
  status: string;
  search_id: number;
  summary: string | any;
  professional_history: string | any;
  companies: string | any;
  investments: string | any;
  football_connections: string | any;
  adverse_media: string | any;
  associates: string | any;
  financial_information: string | any;
  identity_confidence: string;
  potential_namesakes: Array<string | { name: string; evidence?: string }> | string;
  sources: {
    title: string;
    url: string;
    key_points: string;
  }[];
}

// Define the shape of a search history item
interface SearchHistoryItem {
  id: number;
  person_name: string;
  company_name?: string;
  nationality?: string;
  date_of_birth?: string;
  status: string;
  created_at: string;
}

// Define the context type
interface OADSearchContextType {
  // Search inputs
  personName: string;
  setPersonName: (name: string) => void;
  companyName: string;
  setCompanyName: (name: string) => void;
  nationality: string;
  setNationality: (nationality: string) => void;
  dateOfBirth: string;
  setDateOfBirth: (dob: string) => void;
  
  // Search state
  isLoading: boolean;
  searchId: number | null;
  searchStatus: string | null;
  progress: number;
  searchResult: OADSearchResult | null;
  error: string | null;
  
  // Search history
  searchHistory: SearchHistoryItem[];
  activeTab: 'search' | 'history';
  setActiveTab: (tab: 'search' | 'history') => void;
  
  // Actions
  handleSearch: () => Promise<void>;
  clearSearch: () => void;
  viewHistoricalSearch: (searchId: number) => Promise<void>;
  fetchSearchHistory: () => Promise<void>;
  
  // OAD More Information
  isRequestingInfo: boolean;
  requestMoreInfo: () => Promise<void>;
}

// Create the context
const OADSearchContext = createContext<OADSearchContextType | undefined>(undefined);

// Provider component
export function OADSearchProvider({ children }: { children: React.ReactNode }) {
  // Search inputs
  const [personName, setPersonName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [nationality, setNationality] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  
  // Search state
  const [isLoading, setIsLoading] = useState(false);
  const [searchId, setSearchId] = useState<number | null>(null);
  const [searchStatus, setSearchStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [searchResult, setSearchResult] = useState<OADSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  
  // Search history
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<'search' | 'history'>('search');
  
  // OAD More Information
  const [isRequestingInfo, setIsRequestingInfo] = useState(false);
  
  const { getToken, user } = useAuth();
  const { toast } = useToast();
  
  // Function to start a new OAD search
  const handleSearch = async () => {
    if (!personName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a person name',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSearchResult(null);
      
      const token = await getToken();
      if (!token) {
        setError('Not authenticated');
        setIsLoading(false);
        return;
      }

      // Prepare search parameters
      const searchParams = {
        person_name: personName,
        company_name: companyName || undefined,
        nationality: nationality || undefined,
        date_of_birth: dateOfBirth || undefined,
        search_parameters: {
          include_social_media: true,
          max_results_per_query: 10
        }
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/oad/search/async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(searchParams)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setSearchId(data.search_id);
        setSearchStatus('in_progress');
        setProgress(0);
        
        // Start polling for status
        startPolling(data.search_id);
      } else {
        throw new Error(data.error_message || 'Failed to start search');
      }
    } catch (error) {
      console.error('Error starting search:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start search',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to poll for search status
  const startPolling = useCallback(async (searchId: number) => {
    if (isPolling) return;
    
    setIsPolling(true);
    
    const pollInterval = setInterval(async () => {
      try {
        const token = await getToken();
        if (!token) {
          clearInterval(pollInterval);
          setIsPolling(false);
          setError('Not authenticated');
          return;
        }

        // In development, use relative URLs to leverage the Vite proxy
        const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
        const response = await fetch(`${baseUrl}/ai/v1/oad/search/async/${searchId}/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.status === 'success' && data.job) {
          setSearchStatus(data.job.status);
          
          // Update progress if available
          if (data.progress && data.progress.progress_percentage) {
            setProgress(data.progress.progress_percentage);
          }
          
          // If search is completed, fetch results and stop polling
          if (data.job.status === 'completed') {
            clearInterval(pollInterval);
            setIsPolling(false);
            fetchSearchResults(searchId);
          } 
          // If search failed or was cancelled, stop polling
          else if (data.job.status === 'failed' || data.job.status === 'cancelled') {
            clearInterval(pollInterval);
            setIsPolling(false);
            setError(`Search ${data.job.status}: ${data.job.error_message || 'No error details available'}`);
          }
        }
      } catch (error) {
        console.error('Error polling search status:', error);
        clearInterval(pollInterval);
        setIsPolling(false);
        setError(error instanceof Error ? error.message : 'Failed to check search status');
      }
    }, 3000); // Poll every 3 seconds
    
    // Return a cleanup function
    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [getToken, isPolling, toast]);

  // Function to fetch search results
  const fetchSearchResults = async (searchId: number) => {
    try {
      const token = await getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/oad/search/async/${searchId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setSearchResult(data);
        
        // Refresh search history
        fetchSearchHistory();
        
        toast({
          title: 'Search completed',
          description: 'Results are now available',
        });
      } else if (data.status === 'pending') {
        // If still pending, continue polling
        setSearchStatus('in_progress');
        startPolling(searchId);
      } else {
        throw new Error(data.error_message || 'Failed to retrieve search results');
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch search results');
    }
  };

  // Function to fetch search history
  const fetchSearchHistory = async () => {
    try {
      const token = await getToken();
      if (!token) {
        return;
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/oad/search/history?page=0&page_size=10&sort_by=created_at&sort_order=desc`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.search_history) {
        setSearchHistory(data.search_history);
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
      // Don't set error state here to avoid disrupting the main flow
    }
  };

  // Load search history when component mounts or tab changes to history
  useEffect(() => {
    if (activeTab === 'history') {
      fetchSearchHistory();
    }
  }, [activeTab]);

  // Function to view a historical search
  const viewHistoricalSearch = async (searchId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = await getToken();
      if (!token) {
        setError('Not authenticated');
        setIsLoading(false);
        return;
      }

      // Find the history item to get the person name
      let historyItem = searchHistory.find(item => item.id === searchId);
      
      // If we don't have the history item, fetch the search history first
      if (!historyItem) {
        await fetchSearchHistory();
        historyItem = searchHistory.find(item => item.id === searchId);
      }
      
      // Update the search inputs if we found the history item
      if (historyItem) {
        // Update the search inputs to match the historical search
        setPersonName(historyItem.person_name);
        if (historyItem.company_name) setCompanyName(historyItem.company_name);
        if (historyItem.nationality) setNationality(historyItem.nationality);
        if (historyItem.date_of_birth) setDateOfBirth(historyItem.date_of_birth);
      }

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/ai/v1/oad/search/async/${searchId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        // If we still don't have the person name from history, try to extract it from the results
        if (!historyItem && data.person_name) {
          setPersonName(data.person_name);
        }
        
        setSearchResult(data);
        setActiveTab('search'); // Switch to search tab to show results
      } else {
        throw new Error(data.error_message || 'Failed to retrieve search results');
      }
    } catch (error) {
      console.error('Error fetching historical search:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch historical search');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to clear the search
  const clearSearch = () => {
    setPersonName('');
    setCompanyName('');
    setNationality('');
    setDateOfBirth('');
    setSearchId(null);
    setSearchStatus(null);
    setProgress(0);
    setSearchResult(null);
    setError(null);
  };
  
  // Function to request more information about an OAD search
  const requestMoreInfo = async () => {
    if (!searchResult) {
      toast({
        title: 'Error',
        description: 'No search results to request information about',
        variant: 'destructive',
        duration: 3000,
      });
      return;
    }

    try {
      setIsRequestingInfo(true);
      setError(null);
      
      const token = await getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // First, ensure we have the correct search information
      // This is especially important when viewing from history
      let currentSearchId = searchResult.search_id;
      let currentPersonName = personName;
      let currentCompanyName = companyName;
      let currentNationality = nationality;
      let currentDateOfBirth = dateOfBirth;
      
      // If we have a search ID, find the corresponding history item to get complete information
      if (currentSearchId) {
        // Fetch the latest search history if needed
        if (searchHistory.length === 0) {
          await fetchSearchHistory();
        }
        
        // Find the matching history item
        const historyItem = searchHistory.find(item => item.id === currentSearchId);
        if (historyItem) {
          // Always use the person name from history as it's the most reliable source
          currentPersonName = historyItem.person_name;
          
          // Use other fields from history if our current state is empty
          if (!currentCompanyName && historyItem.company_name) {
            currentCompanyName = historyItem.company_name;
          }
          if (!currentNationality && historyItem.nationality) {
            currentNationality = historyItem.nationality;
          }
          if (!currentDateOfBirth && historyItem.date_of_birth) {
            currentDateOfBirth = historyItem.date_of_birth;
          }
        }
      }
      
      // If we still don't have a person name (very unlikely), try to extract it from the search result
      if (!currentPersonName && searchResult) {
        if (typeof searchResult.summary === 'object' && searchResult.summary?.person_name) {
          currentPersonName = searchResult.summary.person_name;
        }
      }
      
      // Final validation - we must have a search term
      if (!currentPersonName) {
        throw new Error('Could not determine the person name for this search');
      }

      // Prepare the search results summary
      const searchResultsSummary = JSON.stringify({
        person_name: currentPersonName,
        company_name: currentCompanyName || undefined,
        nationality: currentNationality || undefined,
        date_of_birth: currentDateOfBirth || undefined,
        identity_confidence: searchResult.identity_confidence,
        summary: searchResult.summary,
        search_id: currentSearchId
      }, null, 2);

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      const response = await fetch(`${baseUrl}/api/v1/oad/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          searchTerm: currentPersonName,
          searchResults: searchResultsSummary,
          userId: user?.id,
          userEmail: user?.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      toast({
        title: 'Request Submitted',
        description: data.message || 'Your request for more information has been submitted successfully.',
        duration: 3000, // Auto-dismiss after 3 seconds
      });
    } catch (error) {
      console.error('Error requesting more information:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      
      toast({
        title: 'Request Failed',
        description: error instanceof Error ? error.message : 'Failed to submit your request. Please try again.',
        variant: 'destructive',
        duration: 3000, // Auto-dismiss after 3 seconds
      });
    } finally {
      setIsRequestingInfo(false);
    }
  };

  // Create the context value
  const contextValue: OADSearchContextType = {
    // Search inputs
    personName,
    setPersonName,
    companyName,
    setCompanyName,
    nationality,
    setNationality,
    dateOfBirth,
    setDateOfBirth,
    
    // Search state
    isLoading,
    searchId,
    searchStatus,
    progress,
    searchResult,
    error,
    
    // Search history
    searchHistory,
    activeTab,
    setActiveTab,
    
    // Actions
    handleSearch,
    clearSearch,
    viewHistoricalSearch,
    fetchSearchHistory,
    
    // OAD More Information
    isRequestingInfo,
    requestMoreInfo
  };

  return (
    <OADSearchContext.Provider value={contextValue}>
      {children}
    </OADSearchContext.Provider>
  );
}

// Hook for using the context
export function useOADSearch() {
  const context = useContext(OADSearchContext);
  if (context === undefined) {
    throw new Error('useOADSearch must be used within an OADSearchProvider');
  }
  return context;
}